<script lang="ts" setup>
import { onBeforeUnmount, onMounted } from 'vue'

export interface SancareSidePanelProps {
  width?: string
  duration?: number
}

const props = withDefaults(defineProps<SancareSidePanelProps>(), {
  width: '400px',
  duration: 300,
})

// Two-way bound so this component can also close itself (outside click / Escape). A trigger
// that opens/switches the panel must call `@click.stop` on its own handler (same contract as
// SancareModal / v-on-click-outside) — otherwise the directive sees that same click and races
// it shut again.
const open = defineModel<boolean>('open', { default: false })

function closePanel() {
  open.value = false
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && open.value) {
    open.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <teleport to="body">
    <transition name="sancare-side-panel">
      <div
        v-if="open"
        v-on-click-outside="closePanel"
        class="sancare-side-panel"
        :style="{ width: props.width, transitionDuration: `${duration}ms` }"
      >
        <slot />
      </div>
    </transition>
  </teleport>
</template>

<style lang="scss" scoped>
.sancare-side-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  overflow: hidden;
  background-color: #ffffff;
  border-left: 1px solid var(--bs-border-color);
  box-shadow: -2px 0 6px rgba(0, 0, 0, .15);
  transition-property: transform;
  transition-timing-function: ease;

  &-enter-from,
  &-leave-to {
    transform: translateX(100%);
  }
}
</style>
